/**
 * Dashboard: at-a-glance list of every enrolled endpoint in the family with
 * status, last heartbeat, and platform info. Refreshes on mount and every
 * 30 seconds while mounted so parents see devices come online without a
 * manual reload.
 */

import { useEffect, useState } from 'preact/hooks';
import { AddDeviceDialog } from '../components/AddDeviceDialog';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Input } from '../components/Input';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { cn } from '../lib/cn';
import {
  activityByEndpoint,
  endpoints,
  needsFamilyKeyUnlock,
  refresh,
  removeEndpoint,
  renameEndpoint,
  supportsDeviceConfig,
  DEVICE_CONFIG_MIN_VERSION,
  type Endpoint,
} from '../data/endpoints';
import {
  currentConfig,
  fetchLatest,
  overrideCount,
  save as saveConfig,
} from '../data/config';
import { setDeviceOverride, syncDeviceConfigs } from '../data/deviceConfig';
import { useVisiblePoll } from '../hooks/useVisiblePoll';
import {
  authSession,
  familyKeyInMemory,
  loadFamilyKey,
} from '../state/session';

const POLL_INTERVAL_MS = 30_000;

// If no heartbeat within this window we render the endpoint as stale
// regardless of what the server column says. `endpoints.status` on the
// CP is only ever bumped to 'healthy' on a heartbeat and nothing ever
// downgrades it — a device that goes offline stays green in the DB
// until it comes back. Compute freshness client-side instead.
const STALE_HEARTBEAT_MS = 3 * 60_000;

function effectiveStatus(
  serverStatus: string,
  lastHeartbeatAt: string | null,
): string {
  if (lastHeartbeatAt === null) return 'never seen';
  const ageMs = Date.now() - new Date(lastHeartbeatAt).getTime();
  if (ageMs > STALE_HEARTBEAT_MS) return 'stale';
  return serverStatus;
}

function toneForStatus(status: string): 'ok' | 'warn' | 'neutral' {
  if (status === 'healthy') return 'ok';
  if (status === 'stale' || status === 'offline') return 'warn';
  return 'neutral';
}

/**
 * Has this device picked up the config the parent last saved?
 *
 * Answerable only because the heartbeat now persists
 * `config_version_applied`; before plan 034 the column was NULL on every
 * row and this line could not have been written honestly. Returns null when
 * there is nothing useful to say yet — a device with no document, or one
 * that has not heartbeated since the deploy.
 */
function configStateLabel(e: Endpoint): string | null {
  if (e.config_version === null) return null;
  if (e.config_version_applied === null) return 'Settings pending';
  return e.config_version_applied >= e.config_version
    ? 'Settings applied'
    : 'Settings pending';
}

function formatHeartbeat(iso: string | null): string {
  if (iso === null) return 'never';
  return new Date(iso).toLocaleString();
}

function timeAgo(iso: string): string {
  const deltaMs = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.floor(deltaMs / 1000));
  if (s < 60) return `${String(s)}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${String(d)}d ago`;
  const w = Math.floor(d / 7);
  if (w < 52) return `${String(w)}w ago`;
  const y = Math.floor(d / 365);
  return `${String(y)}y ago`;
}

/**
 * Compact per-endpoint activity ledger: shows the last few tick outcomes as
 * small dots (poppy = flagged, muted = clean) with a summary line. Empty
 * state renders nothing rather than a placeholder — the dashboard row's
 * "Last screenshot: never" already communicates that case.
 *
 * Paused / off-hours entries arrive with the `category` field set to the
 * scheduler sentinels (see `crates/backstop-core/src/scheduler.rs`). We
 * render those as an amber dot with a distinct tooltip so parents can see
 * their policy choices being respected in the ledger rather than assuming
 * the endpoint is offline.
 */
const PAUSED_SENTINEL = '__paused';
const OFF_HOURS_SENTINEL = '__off_hours';

function ActivityStrip({ endpointId }: { endpointId: string }) {
  const rows = activityByEndpoint.value[endpointId] ?? [];
  if (rows.length === 0) return null;
  const flagged = rows.filter((r) => r.flagged).length;
  const paused = rows.filter(
    (r) => r.category === PAUSED_SENTINEL || r.category === OFF_HOURS_SENTINEL,
  ).length;
  const latest = rows[0]; // server returns most-recent first
  return (
    // Stack dots-above-text on mobile so the summary doesn't wrap into
    // three lines beside a fixed column of dots. Side-by-side from sm: up.
    <div class="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
      <div class="flex items-center gap-0.5" aria-hidden="true">
        {rows.slice(0, 10).map((r) => {
          const isPaused =
            r.category === PAUSED_SENTINEL || r.category === OFF_HOURS_SENTINEL;
          let tone: string;
          let title: string;
          if (r.flagged) {
            tone = 'bg-signal';
            title = `${new Date(r.at).toLocaleString()} — flagged`;
          } else if (isPaused) {
            tone = 'bg-amber-500/70';
            title = `${new Date(r.at).toLocaleString()} — ${r.category === PAUSED_SENTINEL ? 'paused' : 'off hours'}`;
          } else {
            tone = 'bg-surface-muted/40';
            title = `${new Date(r.at).toLocaleString()} — clean`;
          }
          return (
            <span
              key={r.at}
              title={title}
              class={'inline-block h-1.5 w-1.5 rounded-full ' + tone}
            />
          );
        })}
      </div>
      <div class="text-surface-muted text-[11px]">
        {String(rows.length)} scans · {String(flagged)} flagged
        {paused > 0 ? ` · ${String(paused)} paused` : ''} · last{' '}
        {latest !== undefined ? timeAgo(latest.at) : 'never'}
      </div>
    </div>
  );
}

/**
 * Family-wide pause. Distinct from the per-device toggles on each card:
 * this writes `collection_enabled` into every device's resolved document,
 * so it wins regardless of what an individual device is set to.
 *
 * Semantics, stated because they are easy to get subtly wrong: pausing
 * remembers nothing. There is no hidden "what was it before?" to restore,
 * and resuming turns every device back on. Each device's own state is
 * visible on its own card, so nothing is hidden by the bulk action.
 *
 * Disabled until the config has been fetched at least once, so we can't
 * accidentally save a `DEFAULT_CONFIG` from a stale render.
 */
function PauseAllCard() {
  const cfg = currentConfig.value;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const devices = endpoints.value.filter((e) => e.status !== 'revoked');
  const running = devices.filter(
    (e) =>
      (cfg?.device_overrides[e.id]?.collection_enabled ??
        cfg?.collection_enabled ??
        true) === true,
  ).length;
  const anyRunning = running > 0;

  // Nothing to pause, and the empty-devices message belongs to the list
  // below rather than being said twice.
  if (devices.length === 0) return null;

  async function setAll(enabled: boolean): Promise<void> {
    if (cfg === null) return;
    setBusy(true);
    setError(null);
    try {
      // Clear every per-device override of this one section, then set the
      // family value. Leaving the overrides in place would make "Pause all"
      // silently skip exactly the devices a parent most wanted paused.
      const nextOverrides: typeof cfg.device_overrides = {};
      for (const [id, ov] of Object.entries(cfg.device_overrides)) {
        const { collection_enabled: _dropped, ...rest } = ov;
        if (Object.keys(rest).length > 0) nextOverrides[id] = rest;
      }
      const next = {
        ...cfg,
        collection_enabled: enabled,
        device_overrides: nextOverrides,
      };
      await saveConfig(next);
      const results = await syncDeviceConfigs(devices.map((e) => e.id));
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setError(
          `${String(failed.length)} of ${String(results.length)} devices did not update. They will retry on the next refresh.`,
        );
      }
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card class="bg-surface-2 space-y-2">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div class="text-lg font-semibold">
            {`Collection is on for ${String(running)} of ${String(devices.length)} device${devices.length === 1 ? '' : 's'}`}
          </div>
          <div class="text-surface-muted max-w-[60ch] text-sm">
            Pausing everything overrides each device's own setting until you
            resume.
          </div>
        </div>
        <div class="w-full shrink-0 sm:w-auto">
          <Button
            onClick={() => {
              void setAll(!anyRunning);
            }}
            disabled={busy || cfg === null || devices.length === 0}
            variant={anyRunning ? 'danger' : 'primary'}
            size="md"
            class="w-full sm:w-auto"
          >
            {busy ? 'Saving…' : anyRunning ? 'Pause all' : 'Resume all'}
          </Button>
        </div>
      </div>
      {error !== null ? (
        <p class="text-signal text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </Card>
  );
}

/**
 * Per-device pause. Writes only this device's `collection_enabled`
 * override, leaving every other section inherited.
 */
function DeviceToggle({ endpointId }: { endpointId: string }) {
  const cfg = currentConfig.value;
  const [busy, setBusy] = useState(false);
  if (cfg === null) return null;

  const override = cfg.device_overrides[endpointId];
  const enabled = override?.collection_enabled ?? cfg.collection_enabled;

  async function toggle(): Promise<void> {
    setBusy(true);
    try {
      await setDeviceOverride(
        endpointId,
        { ...override, collection_enabled: !enabled },
        saveConfig,
      );
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      size="sm"
      variant={enabled ? 'danger' : 'primary'}
      // Fills its grid track on the device card so Pause and Resume occupy
      // the same box; see the cluster comment in the card markup.
      class="sm:w-full"
      disabled={busy}
      onClick={() => {
        void toggle();
      }}
      aria-label={enabled ? 'Pause this device' : 'Resume this device'}
    >
      {busy ? '…' : enabled ? 'Pause' : 'Resume'}
    </Button>
  );
}

export function DashboardRoute() {
  const [addOpen, setAddOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockRemember, setUnlockRemember] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
    // Seed the kill-switch card. Ignore errors — the card falls back to
    // "on" until it has a real config to render.
    void fetchLatest().catch(() => {
      /* handled by fetchLatest's own fallthrough */
    });
    // If the family key rehydrates asynchronously (from the sessionStorage
    // "remember" cache), retrigger a refresh so any pending endpoints get
    // their wraps delivered without waiting on the 30s poll.
    let prevFk = familyKeyInMemory.value;
    const unsub = familyKeyInMemory.subscribe((fk) => {
      if (prevFk === null && fk !== null) {
        void refresh();
      }
      prevFk = fk;
    });
    return () => {
      unsub();
    };
  }, []);

  // Only polls while the tab is visible; refetches on the way back. See
  // useVisiblePoll — a backgrounded tab polling forever was keeping the
  // control plane warm and the database from ever autosuspending.
  useVisiblePoll(() => {
    void refresh();
  }, POLL_INTERVAL_MS);

  async function onUnlock(e: Event): Promise<void> {
    e.preventDefault();
    setUnlocking(true);
    setUnlockError(null);
    try {
      const email = authSession.value?.email;
      if (email === undefined) {
        setUnlockError('Not signed in — reload the page.');
        return;
      }
      const ok = await loadFamilyKey(email, unlockPassword, {
        remember: unlockRemember,
      });
      if (!ok) {
        setUnlockError(
          'No encrypted vault on this device. Log out and use "Recover with mnemonic" instead.',
        );
      } else {
        setUnlockPassword('');
        await refresh(); // will wrap any pending endpoints now that fk is live
      }
    } catch {
      setUnlockError('Wrong password.');
    } finally {
      setUnlocking(false);
    }
  }

  function beginRename(id: string, currentLabel: string | null): void {
    setRenamingId(id);
    setRenameDraft(currentLabel ?? '');
    setRenameError(null);
  }

  async function commitRename(id: string): Promise<void> {
    if (renamingId !== id) return;
    const draft = renameDraft.trim();
    // Match server-side cap.
    if (draft.length > 80) {
      setRenameError('Name must be 80 characters or fewer.');
      return;
    }
    try {
      await renameEndpoint(id, draft);
      setRenamingId(null);
      setRenameDraft('');
      setRenameError(null);
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : String(err));
    }
  }

  function cancelRename(): void {
    setRenamingId(null);
    setRenameDraft('');
    setRenameError(null);
  }

  async function onConfirmRemove(): Promise<void> {
    if (confirmRemoveId === null) return;
    const id = confirmRemoveId;
    setRemovingId(id);
    try {
      await removeEndpoint(id);
    } finally {
      setRemovingId(null);
      setConfirmRemoveId(null);
    }
  }

  return (
    <main class="mx-auto max-w-4xl space-y-4 p-4">
      {/* Persistent header lives in <AppShell/>; this row is now just the
          page title + primary CTA. */}
      <div class="flex items-center justify-between">
        <h1 class="text-3xl font-semibold">Dashboard</h1>
        <Button
          size="sm"
          onClick={() => {
            setAddOpen(true);
          }}
        >
          + Add device
        </Button>
      </div>
      <OnboardingChecklist />
      <AddDeviceDialog open={addOpen} onOpenChange={setAddOpen} />
      <ConfirmDialog
        open={confirmRemoveId !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmRemoveId(null);
        }}
        title="Remove device"
        body="This device will stop reporting immediately. It would need to be re-installed to reconnect. Your saved alerts are kept."
        confirmLabel="Remove"
        destructive
        onConfirm={onConfirmRemove}
      />

      {needsFamilyKeyUnlock.value && (
        <div class="mx-auto flex w-full max-w-lg overflow-hidden rounded-lg border border-line bg-surface shadow-xl">
          <div aria-hidden="true" class="w-1 shrink-0 self-stretch bg-signal" />
          <div class="flex-1 space-y-4 p-6">
            <p class="font-mono text-[11px] tracking-[0.14em] uppercase text-signal">
              Encryption locked
            </p>
            <p class="text-sm text-surface-fg">
              A device enrolled but is waiting on the family key to finish
              setup. Enter your password so we can deliver it.
            </p>
            <form onSubmit={onUnlock} class="space-y-3">
              <Input
                type="password"
                value={unlockPassword}
                onInput={(e: Event) => {
                  setUnlockPassword((e.target as HTMLInputElement).value);
                }}
                placeholder="Your password"
                autocomplete="current-password"
              />
              <label class="flex items-center gap-2 text-[13px] text-surface-body">
                <input
                  type="checkbox"
                  checked={unlockRemember}
                  onInput={(e: Event) => {
                    setUnlockRemember((e.target as HTMLInputElement).checked);
                  }}
                  class="h-4 w-4 rounded border-line-strong accent-signal focus:ring-signal"
                />
                Remember on this device for 12 hours
              </label>
              <div class="flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  disabled={unlocking || unlockPassword.length === 0}
                >
                  {unlocking ? 'Unlocking…' : 'Unlock'}
                </Button>
              </div>
            </form>
            {unlockError !== null && (
              <p class="text-[13px] text-signal" role="alert">
                {unlockError}
              </p>
            )}
          </div>
        </div>
      )}

      {!needsFamilyKeyUnlock.value && <PauseAllCard />}

      <div
        class={cn(
          'grid gap-3 transition',
          needsFamilyKeyUnlock.value &&
            'pointer-events-none select-none opacity-40',
        )}
        aria-hidden={needsFamilyKeyUnlock.value ? 'true' : undefined}
      >
        {endpoints.value.map((e) => (
          <Card
            key={e.id}
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <div class="min-w-0">
              {renamingId === e.id ? (
                <div class="space-y-1">
                  <Input
                    autoFocus
                    value={renameDraft}
                    onInput={(ev: Event) => {
                      setRenameDraft((ev.target as HTMLInputElement).value);
                    }}
                    onBlur={() => {
                      void commitRename(e.id);
                    }}
                    onKeyDown={(ev: KeyboardEvent) => {
                      if (ev.key === 'Enter') {
                        ev.preventDefault();
                        void commitRename(e.id);
                      } else if (ev.key === 'Escape') {
                        ev.preventDefault();
                        cancelRename();
                      }
                    }}
                    placeholder="Device name"
                    maxLength={80}
                    class="font-medium"
                    aria-label="Device name"
                  />
                  {renameError !== null && (
                    <p class="text-signal text-[12px]">{renameError}</p>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    beginRename(e.id, e.label);
                  }}
                  class="font-medium text-left rounded hover:bg-surface-2 px-1 -mx-1 py-0.5 transition-colors"
                  title="Click to rename"
                >
                  {e.label ?? 'Unnamed device'}
                </button>
              )}
              <div class="text-surface-muted text-sm">
                {e.platform} · v{e.agent_version}
              </div>
              <div class="text-surface-muted text-xs">
                Last heartbeat: {formatHeartbeat(e.last_heartbeat_at)}
              </div>
              <div class="text-surface-muted text-xs">
                Last screenshot: {formatHeartbeat(e.last_screenshot_at)}
                {configStateLabel(e) !== null ? (
                  <> · {configStateLabel(e)}</>
                ) : null}
              </div>
              <ActivityStrip endpointId={e.id} />
              {/* An override on a pre-0.0.64 binary is stored but not
                  applied. Showing a tick there would read as "the product
                  is broken" the first time a parent checked. */}
              {overrideCount(currentConfig.value?.device_overrides[e.id]) > 0 &&
              !supportsDeviceConfig(e.agent_version) ? (
                <div class="mt-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800">
                  <span class="font-semibold">
                    Custom settings saved, but not applied.
                  </span>{' '}
                  Needs Backstop {DEVICE_CONFIG_MIN_VERSION} or later — it's on{' '}
                  {e.agent_version}.
                </div>
              ) : null}
            </div>
            {/* Fixed track widths, and every control fills its track.

                As a right-aligned flex row this cluster sized each control
                to its own text, so nothing lined up between cards: "stale"
                is narrower than "healthy", "Resume" wider than "Pause", and
                each difference shifted everything to its left.

                Cards are separate subtrees, so they cannot share a grid
                (that would want subgrid and one grid over the whole list).
                Identical explicit tracks get the same result: each control
                lands in the same column on every card whatever it says.

                The controls stretch (`sm:w-full`) rather than sitting at the
                left of their track. Left-aligned, a short value left its
                slack as a visible gap before the next column — "stale" in a
                track sized for "never seen" opened 56px — so the spacing
                read as uneven even though the columns were aligned. Filling
                the track puts every gap at exactly the column gap.

                Track widths are the measured intrinsic width of the longest
                string each column can hold (Resume 77, "never seen" 79,
                "Family defaults" 103, "Removing…" 74), rounded up. Sizing
                them by eye instead cost ~76px of dead width, which squeezed
                the details on the left into wrapping.

                Below `sm` it stays a wrapping flex row, hence `sm:`. */}
            <div class="flex shrink-0 flex-wrap items-center justify-between gap-3 sm:grid sm:grid-cols-[5rem_5.25rem_6.75rem_4.75rem_4.75rem] sm:items-center">
              <DeviceToggle endpointId={e.id} />
              {(() => {
                const s = effectiveStatus(e.status, e.last_heartbeat_at);
                return (
                  <Badge
                    tone={toneForStatus(s)}
                    class="sm:w-full sm:justify-center"
                  >
                    {s}
                  </Badge>
                );
              })()}
              {(() => {
                const n = overrideCount(
                  currentConfig.value?.device_overrides[e.id],
                );
                return n > 0 ? (
                  <Badge tone="danger" class="sm:w-full sm:justify-center">
                    {n} custom
                  </Badge>
                ) : (
                  <Badge class="sm:w-full sm:justify-center">
                    Family defaults
                  </Badge>
                );
              })()}
              <a
                href={`/devices/${e.id}`}
                class="text-surface-muted hover:text-surface-fg font-mono text-[11px] tracking-[0.14em] uppercase transition-colors sm:w-full sm:text-center"
              >
                Configure
              </a>
              <button
                type="button"
                onClick={() => {
                  setConfirmRemoveId(e.id);
                }}
                disabled={removingId === e.id}
                class="text-surface-muted hover:text-signal font-mono text-[11px] tracking-[0.14em] uppercase transition-colors disabled:opacity-50 sm:w-full sm:text-center"
              >
                {removingId === e.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </Card>
        ))}
        {endpoints.value.length === 0 && <Card>No devices enrolled yet.</Card>}
      </div>
    </main>
  );
}
